import { Controller, useForm } from 'react-hook-form'
import { NavLink, useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'

import { api } from '../../services/api'
import { Button } from '../../components/Button'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { createResetPasswordSchema } from '../../validators/resetPasswordSchema'
import { useAuth } from '../../contexts/AuthContext'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../constants/toast'
import { getErrorMessage } from '../../utils/getErrorMessage'

type FormValues = {
  password: string
  passwordConfirmation: string
}

const schema = createResetPasswordSchema({
  password: {
    min: 'Deve conter 8 caracteres, uma maiúscula, uma minúscula, um número e um caractere especial',
    matches:
      'Deve conter 8 caracteres, uma maiúscula, uma minúscula, um número e um caractere especial',
    identical: 'Senhas devem ser idênticas',
    required: 'Campo obrigatório',
  },
  passwordConfirmation: {
    identical: 'Senhas devem ser idênticas',
    required: 'Campo obrigatório',
  },
})

export const ChangePassword = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onSubmit = async (values: FormValues) => {
    try {
      const resourceByUserType = {
        admin: 'admins',
        researcher: 'researchers',
      }[user!.type as 'admin' | 'researcher']

      const response = await api.put(
        `/${resourceByUserType}/update-password/${user!.id}`,
        values
      )
      if ([200, 201].includes(response.status)) {
        toast('Senha redefinida com sucesso!', TOAST_SUCCESS_DEFAULT_OPTIONS)

        navigate('/painel')
      }
    } catch (error) {
      toast(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  return (
    <div className="w-full px-6 lg:px-[15.5rem] py-[55px]">
      <header className="mb-14">
        <h2 className="text-lg font-medium text-grey-800">
          Cadastrar nova senha
        </h2>
      </header>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between w-full h-full max-w-[402px] grow "
      >
        <section className="mb-14">
          <div className="mb-4">
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  autoFocus
                  isValid={!invalid}
                  label="Digite sua nova senha"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="passwordConfirmation"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label="Digite a senha novamente"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
        </section>

        <footer className="flex flex-col flex-wrap gap-2 md:flex-row-reverse">
          <div className="flex-1">
            <Button
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              type="submit"
              className="whitespace-nowrap"
              fullWidth
            >
              Cadastrar nova senha
            </Button>
          </div>

          <div className="flex-1">
            <NavLink to="/painel">
              <Button
                disabled={formState.isSubmitting}
                type="button"
                variant="outline"
                fullWidth
              >
                Cancelar
              </Button>
            </NavLink>
          </div>
        </footer>
      </form>
    </div>
  )
}
